// @flow
import React, { Children, type Node, type Element, useState, useEffect } from 'react';

import { Backdrop } from 'Components/frameworks/backdrop/Backdrop';
import { Confirmation } from 'Components/widgets/confirmation/Confirmation';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { Layer } from 'Components/frameworks/layer/Layer';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
import { t } from 'Src/i18n';

export type ProductInformationLayerProps = {
  activeItem: number,
  backNavigationLabel?: string,
  children: Node,
  errorMessage?: string,
  renderDetail: (data: Object) => Node,
  toggleLayer: (number, boolean) => void,
};
export const ProductInformationLayer = ({
  activeItem,
  backNavigationLabel,
  children,
  errorMessage,
  renderDetail,
  toggleLayer,
}: ProductInformationLayerProps) => {
  const [state, setState] = useState({
    activeItem,
    isLoading: false,
    isOpen: false,
  });

  useEffect(
    () => {
      loadDetails();
    },
    [activeItem],
  );

  const closeLayer = () => {
    setState({
      ...state,
      isLoading: false,
      isOpen: false,
    });
    // -1 to indicate that no product has been selected
    toggleLayer(-1, false);
  };

  const updateDetail = (active: number, detailInfo: Object): void => {
    setState({
      activeItem: active,
      detail: detailInfo,
      isLoading: false,
      isOpen: true,
    });
  };

  const getItem = (index: number): Element<*> => {
    return Children.toArray(children)[index];
  };

  const loadDetails = (): void => {
    const item = getItem(activeItem) || {};
    const { props } = item;
    if (props && props.fetchDetail) {
      setState({ ...state, isLoading: true });
      props
        .fetchDetail()
        .then(data => {
          updateDetail(activeItem, data);
        })
        .catch(() => {
          updateDetail(activeItem, null);
        });
    }
  };

  const renderActiveDetail = () => {
    const item = getItem(activeItem);

    if (!item) {
      return null;
    }

    const leftButton = {
      iconClassName: 'rtl:-scale-x-1',
      iconName: ICONS.LEFT_ARROW,
      onClick: closeLayer,
      label: backNavigationLabel,
    };

    const ariaLabel = state.detail ? state.detail.label || state.detail.title : '';

    return (
      <Layer
        ariaLabel={ariaLabel}
        bgColor={COLORS.WHITE}
        buttonLeft={leftButton}
        closeLabel={t('common.close')}
        hasAnimateEnter
        hasContainerHeader
        onClose={closeLayer}
      >
        {state.detail ? (
          renderDetail(state.detail)
        ) : (
          <div data-testid="error" className="p-5">
            <div className="max-w-site mx-auto flex justify-center">
              <Confirmation status="error" title={errorMessage} />
            </div>
          </div>
        )}
      </Layer>
    );
  };

  return state.isOpen && !state.isLoading
    ? renderActiveDetail()
    : state.isLoading && (
        <div id="loading" className="relative z-4">
          <Backdrop bgColor="#fff" opacity={0.95}>
            <div
              aria-live="polite"
              className="absolute text-center top-1/2 left-1/2 -translate-50"
              style={{ width: '13rem' }}
            >
              <span className="a11y">Loading</span>
              <Spinner data-testid="Spinner" iconName={ICONS.TRIDENT1} />
            </div>
          </Backdrop>
        </div>
      );
};
